import "../stylesheets/App.css";
import "../stylesheets/Ranking.css";
import "../stylesheets/SliderStages.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import StageItem from "./subcomponents/StageItem.js";
import UserItem from "./subcomponents/UserItem.js";
import Header from "./subcomponents/Header.js";
import Loader from "./subcomponents/Loader.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useNavigate } from "react-router-dom";

function RankingFase() {
  const navigate = useNavigate();
  const sliderRef = useRef(null);

  const userInfo = useMemo(() => {
    const raw = localStorage.getItem("user-info");
    return raw ? JSON.parse(raw) : null;
  }, []);

  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingStages, setLoadingStages] = useState(true);
  const [loadingIsAdmin, setLoadingIsAdmin] = useState(true);

  const [activePage, setActivePage] = useState(1);
  const [pages, setPages] = useState([1]);

  const [totalUsers, setTotalUsers] = useState([]);
  const [users, setUsers] = useState([]);

  const [stages, setStages] = useState([]);
  const [activeStageId, setActiveStageId] = useState(null);

  const [isAdmin, setIsAdmin] = useState(0);

  const apiCall = async (url) => {
    const res = await fetch(url);
    return res.json();
  };

  // ✅ Guard + UI tweaks (evita crash si no hay user-info)
  useEffect(() => {
    if (!userInfo) {
      navigate("/login");
      return;
    }

    const search = document.getElementById("search-container");
    if (search) search.style.display = "none";

    const run = async () => {
      try {
        const [stagesRes, adminRes] = await Promise.all([
          apiCall(`${process.env.REACT_APP_API_BASE_URL}/stages/list`),
          apiCall(
            `${process.env.REACT_APP_API_BASE_URL}/admin/is-admin/${userInfo.idUser}`
          ),
        ]);

        setStages(stagesRes?.data || []);
        setLoadingStages(false);

        setIsAdmin(adminRes?.data ? 1 : 0);
        setLoadingIsAdmin(false);

        // ✅ seteo fase por defecto (no asume idStage secuencial)
        const list = stagesRes?.data || [];
        const firstActive = list.find((s) => s.active === 1) || list[0];
        if (firstActive?.idStage) setActiveStageId(firstActive.idStage);
      } catch (e) {
        console.log(e);
        setLoadingStages(false);
        setLoadingIsAdmin(false);
      }
    };

    run();
  }, [navigate, userInfo]);

  // ✅ Carga ranking por fase (cuando cambia la fase)
  useEffect(() => {
    if (!activeStageId) return;

    setLoadingUsers(true);

    const run = async () => {
      try {
        const data = await apiCall(
          `${process.env.REACT_APP_API_BASE_URL}/users/orderListByStage/1/${activeStageId}`
        );

        const list = data?.data || [];
        setTotalUsers(list);

        // ✅ paginación robusta (usa data.total si viene, si no usa length)
        const total = data?.total ?? list.length;
        const cant = total % 20 === 0 ? Math.trunc(total / 20) : Math.trunc(total / 20) + 1;
        setPages(Array.from({ length: Math.max(cant, 1) }, (_, i) => i + 1));

        setActivePage(1);
        setUsers(list.slice(0, 20));
      } catch (e) {
        console.log(e);
      } finally {
        setLoadingUsers(false);
      }
    };

    run();
  }, [activeStageId]);

  // ✅ pagina cambia -> slice (y también cuando cambia totalUsers)
  useEffect(() => {
    setUsers(totalUsers.slice((activePage - 1) * 20, activePage * 20));
  }, [activePage, totalUsers]);

  const slideLeft = () => {
    const slider = sliderRef.current;
    if (!slider) return;

    const item = slider.querySelector(".stage-item-container");
    const distancia = item ? item.clientWidth : 0;
    if (!distancia) return;

    slider.scrollLeft =
      slider.scrollLeft + slider.clientWidth === slider.scrollWidth
        ? slider.scrollLeft - distancia
        : slider.scrollLeft - distancia - 20;
  };

  const slideRight = () => {
    const slider = sliderRef.current;
    if (!slider) return;

    const item = slider.querySelector(".stage-item-container");
    const distancia = item ? item.clientWidth : 0;
    if (!distancia) return;

    slider.scrollLeft = slider.scrollLeft + distancia + 20;
  };

  const activeStage = stages.find((s) => s.idStage === activeStageId);

  return (
    <>
      <Header />

      <section className="ranking-slider">
        <div className="relative flex items-center">
          <FontAwesomeIcon
            icon={solid("chevron-left")}
            className="opacity-50 cursor-pointer hover:opacity-100"
            onClick={slideLeft}
            size={"sm"}
          />

          <div
            ref={sliderRef}
            className="w-full h-full overflow-x-scroll scroll whitespace-nowrap scroll-smooth scrollbar-hide"
          >
            {stages.map((item) => (
              <StageItem
                key={item.idStage}
                function_onclick={item.active === 1 ? () => setActiveStageId(item.idStage) : null}
                stage={item}
                is_ranking={true}
              />
            ))}
          </div>

          <FontAwesomeIcon
            icon={solid("chevron-right")}
            className="opacity-50 cursor-pointer hover:opacity-100"
            onClick={slideRight}
            size={"sm"}
          />
        </div>
      </section>

      <section className="users-list">
        <h2 className="subtitles">
          Ranking - {activeStage ? activeStage.name : ""}
        </h2>

        {users.map((user, i) => (
          <UserItem
            user={user}
            key={`user-${user?.idUser ?? i}`}
            index={(activePage - 1) * 20 + i + 1}
            onClick={
              isAdmin === 1
                ? () => navigate(`/admin/detail/${user.idCategory}/${user.idUser}`)
                : undefined
            }
          />
        ))}
      </section>

      <section className="admin-list-paginator-section">
        {pages.length > 1 ? (
          <FontAwesomeIcon
            icon={solid("chevron-left")}
            className="opacity-50 cursor-pointer hover:opacity-100"
            onClick={() => (activePage > 1 ? setActivePage(activePage - 1) : null)}
            size={"sm"}
          />
        ) : null}

        {pages.length > 1
          ? pages.map((page) =>
              page === 1 ||
              (page > activePage - 2 && page < activePage + 2) ||
              page === pages.length ? (
                <button
                  className={activePage === page ? "pag-item active" : "pag-item"}
                  key={`item-${page}`}
                  onClick={(e) => setActivePage(parseInt(e.currentTarget.value))}
                  value={page}
                >
                  {page}
                </button>
              ) : page === activePage - 2 || page === activePage + 2 ? (
                <span key={`dots-${page}`}>...</span>
              ) : null
            )
          : null}

        {pages.length > 1 ? (
          <FontAwesomeIcon
            icon={solid("chevron-right")}
            className="opacity-50 cursor-pointer hover:opacity-100"
            onClick={() =>
              activePage < pages.length ? setActivePage(activePage + 1) : null
            }
            size={"sm"}
          />
        ) : null}
      </section>

      {(loadingStages || loadingIsAdmin || loadingUsers) ? <Loader /> : null}
    </>
  );
}

export default RankingFase;
